import React from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { countryCodes } from "settings/constants/constants";
import { FieldRow, FormField } from "./FormField";
import { getContactErrors, type ContactDetails } from "./contactDetails";

interface ContactResponseFieldsProps {
  contact: ContactDetails;
  onChange: (contact: ContactDetails) => void;
}

/** The switch and, when it is on, who else should get the response. */
export function ContactResponseFields({
  contact,
  onChange,
}: ContactResponseFieldsProps) {
  const update = (patch: Partial<ContactDetails>) =>
    onChange({ ...contact, ...patch });
  const errors = getContactErrors(contact);
  const countryLabel =
    countryCodes.find((c) => c.value === contact.countryCode)?.label ?? "";

  return (
    <div className="rounded-md border border-semantic-border-primary p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-semantic-text-primary">
            Send the response to another person
          </p>
          <p className="text-xs text-semantic-text-secondary">
            We will also share updates on this case with them.
          </p>
        </div>
        <GenericSwitch
          checked={contact.sendResponse}
          onChange={(sendResponse: boolean) => update({ sendResponse })}
        />
      </div>

      {contact.sendResponse && (
        <div className="mt-4 space-y-4">
          <FieldRow>
            <FormField label="Contact name" required error={errors.name}>
              <GenericInput
                placeholder="Full name"
                value={contact.name}
                onChange={(name: string) => update({ name })}
                fullWidth
              />
            </FormField>
            <FormField label="Contact email" required error={errors.email}>
              <GenericInput
                placeholder="name@domain.com"
                type="email"
                value={contact.email}
                onChange={(email: string) => update({ email })}
                fullWidth
              />
            </FormField>
          </FieldRow>
          <FieldRow>
            <FormField label="Country code">
              <GenericDropdown
                items={countryCodes.map((c) => c.label)}
                value={countryLabel}
                onSelect={(label: string) => {
                  const found = countryCodes.find((c) => c.label === label);
                  if (found) update({ countryCode: found.value });
                }}
                defaultLabel="Select country code"
                minWidthClass=""
                maxWidthClass=""
                widthClass="w-full"
                fixedPosition
              />
            </FormField>
            <FormField label="Phone number">
              <GenericInput
                placeholder="Phone number"
                value={contact.phone}
                onChange={(phone: string) => update({ phone })}
                fullWidth
              />
            </FormField>
          </FieldRow>
        </div>
      )}
    </div>
  );
}

export default ContactResponseFields;
